import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { TriangleAlert, Wifi, WifiOff } from "lucide-react";

import useMqttApi from "hooks/use-mqtt-api";
import HefringLogo from "assets/hefring-wordmark.svg";
import { useStore } from "hooks/use-store";
import { PLACEHOLDER, SYSTEM_STATUS_FETCH_INTERVAL_MS } from "utils/constants";
import { Screens } from "navigation/routes";
import { Color } from "utils/theme";
import { SystemStatus } from "types/system-status";
import { RequestTopics } from "services/topics";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "components/ui/card";
import { Button } from "components/ui/button";
import { Spinner } from "components/ui/spinner";
import { SystemStatusModal } from "./system-status-modal";
import { opacity } from "utils/canvas";

// How long to show the loading state before falling back to the empty state
const PAIRING_CODE_LOADING_TIMEOUT_MS = 20_000;

const isPairingCodePending = (code: string) =>
  !code || code === PLACEHOLDER || code === "N/A";

export const PairingScreen = () => {
  const setScreen = useStore((state) => state.setScreen);
  const pairingCode = useStore((state) => state.pairingCode);
  const pairingRequest = useStore((state) => state.pairingRequest);
  const config = useStore((state) => state.config);

  const [status, setStatus] = useState<SystemStatus | null>(null);
  const [showSystemStatusModal, setShowSystemStatusModal] = useState(false);

  const codePending = isPairingCodePending(pairingCode);
  const [codeTimedOut, setCodeTimedOut] = useState(false);

  useEffect(() => {
    if (!codePending) {
      setCodeTimedOut(false);
      return;
    }

    const timeout = setTimeout(
      () => setCodeTimedOut(true),
      PAIRING_CODE_LOADING_TIMEOUT_MS
    );

    return () => clearTimeout(timeout);
  }, [codePending]);

  const { data, fetch } = useMqttApi<{ data: SystemStatus }>({
    requestTopic: RequestTopics.SystemStatus,
    enabled: true,
    onError: (error) => {
      toast.error(error.message);
    },
  });

  useEffect(() => {
    // fetch network status interval
    const interval = setInterval(
      () => fetch(),
      SYSTEM_STATUS_FETCH_INTERVAL_MS
    );

    return () => {
      clearInterval(interval);
    };
  }, [fetch]);

  useEffect(() => {
    // Update state (which might cause re-render and scroll jump)
    if (data?.data) setStatus(data.data);
  }, [data]);

  useEffect(() => {
    if (!pairingRequest) {
      setScreen(Screens.Dashboard);
    }
  }, [pairingRequest]);

  return (
    <section className="h-full w-full flex flex-col p-4 md:p-6 overflow-auto">
      <div className="flex justify-between space-x-4 w-full">
        <img className="h-12" src={HefringLogo} alt="Hefring Logo" />
        <Button
          variant="secondary"
          onClick={() => setShowSystemStatusModal(true)}
        >
          {status?.network.internet ? (
            <Wifi
              className="mr-3 size-5"
              style={{
                color: Color.Green[400],
              }}
            />
          ) : (
            <WifiOff
              className="mr-3 size-5"
              style={{
                color: Color.Primary[400],
              }}
            />
          )}
          System Status
        </Button>
      </div>

      <div className="flex-1 flex-col justify-center items-center flex my-6">
        <Card className="text-center max-w-lg rounded-2xl p-1 md:p-4">
          <CardHeader>
            <h1 className="text-2xl font-medium mb-1">Pair device</h1>
            <p style={{ color: Color.Primary[400] }}>
              This device is not linked to IMAS Console. Log in and enter the
              pairing code below to connect it to the vessel.
            </p>
          </CardHeader>
          <CardContent className="flex flex-col items-center justify-center">
            {codePending && !codeTimedOut ? (
              <div
                className="h-16 min-h-fit px-6 py-2 space-x-3 flex items-center justify-center rounded-lg border"
                style={{
                  background: Color.Primary[700],
                  borderColor: Color.Primary[600],
                  color: Color.Primary[400],
                }}
              >
                <Spinner className="size-5" />
                <p style={{ color: Color.Primary[400] }}>
                  Fetching pairing code…
                </p>
              </div>
            ) : codePending ? (
              <div
                className="min-h-16 px-6 py-3 space-x-3 flex items-center justify-center rounded-lg border"
                style={{
                  background: Color.Primary[700],
                  borderColor: Color.Primary[600],
                }}
              >
                <p
                  className="text-center"
                  style={{ color: Color.Primary[400] }}
                >
                  Pairing code not available yet
                </p>
              </div>
            ) : (
              <div
                className="h-16 min-h-fit w-fit px-4 py-2 space-x-2 flex flex-wrap items-center justify-center rounded-lg border"
                style={{
                  background: Color.Primary[700],
                  borderColor: Color.Primary[600],
                }}
              >
                {pairingCode.split("").map((number, index) => {
                  return (
                    <div key={index}>
                      <h2 className="text-3xl font-medium font-mono tracking-widest">
                        {number}
                      </h2>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
          <CardFooter className="flex-col">
            <p style={{ color: Color.Primary[400] }}>
              Note: An internet connection is required to pair this device.
            </p>
            <p style={{ color: Color.Primary[400] }}>
              This screen will close once pairing has completed.
            </p>
            <p className="text-base mt-2" style={{ color: Color.Primary[500] }}>
              Device ID: {config?.device_id || "Unavailable"}
            </p>
          </CardFooter>
        </Card>

        {status && !status.network.internet && (
          <div
            className="flex items-start mt-4 p-3 rounded-xl max-w-lg border"
            style={{
              background: opacity(Color.Amber[500], 0.1),
              color: Color.Amber[500],
              borderColor: opacity(Color.Amber[500], 0.2),
            }}
          >
            <TriangleAlert className="mr-3 size-6 shrink-0" />
            <p>
              No internet connection. Please ensure the device has an active
              internet connection to complete pairing.
            </p>
          </div>
        )}
      </div>
      <div className="text-center space-y-1">
        <p style={{ color: Color.Primary[400] }}>
          Having trouble pairing? Contact support@hefringmarine.com
        </p>
      </div>

      <SystemStatusModal
        show={showSystemStatusModal}
        onClose={() => setShowSystemStatusModal(false)}
        status={status}
      />
    </section>
  );
};
